// Journal port slice — the workspace's journal told as periods in prose (the
// owner-approved "days as prose" design, 2026-10-01). One period (a day, month
// or year) carries two voices: the author's own text ("Du")
// and the assistant's summary, which the author can correct (a correction locks
// it) and unlock again. The author decides whether the assistant may read their
// own text when it summarizes (consent).
//
// The shapes here are the CLIENT's typed view of the journal read; the HTTP
// slice parses the wire DTO into them at the boundary (parse, don't validate).
// Every write is its own intent (distinct command per intent): writing and
// erasing the author's text, correcting and unlocking the assistant's text,
// allowing and disallowing the assistant to read the author's text.

import type { ResourceRef } from "../../components/Navigation/types.js";
import type { PortResult } from "./result.js";

// === Period keys ==============================================================
// A period is named by its calendar position, never by a free string: a day
// (month 1–12, day 1–31), a month or a year. The HTTP slice parses the wire
// key ("2026-10-01", "2026-10", "2026") into this sum.

type DayKey = {
    readonly scale: "day";
    readonly year: number;
    readonly month: number;
    readonly day: number;
};
type MonthKey = {
    readonly scale: "month";
    readonly year: number;
    readonly month: number;
};
type YearKey = { readonly scale: "year"; readonly year: number };

export type PeriodKey = DayKey | MonthKey | YearKey;
export type JournalScale = PeriodKey["scale"];

// === The assistant's voice ====================================================
// As written by the assistant; corrected by the author (locked: the assistant
// never rewrites it); or corrected and then unlocked (the assistant may write
// it anew on its next pass). Instants are ISO strings.

type AssistantWritten = {
    readonly type: "written";
    readonly markdown: string;
    readonly writtenAt: string;
    // The assistant read the author's own text too (only with consent).
    readonly readOwnText: boolean;
};
// `original`: the assistant's text the correction replaced, kept to show on
// request (null: erased with the author's texts, since it read them).
type AssistantCorrected = {
    readonly type: "corrected";
    readonly markdown: string;
    readonly original: string | null;
    readonly correctedAt: string;
};
type AssistantUnlocked = {
    readonly type: "unlocked";
    readonly markdown: string;
    readonly original: string | null;
    readonly correctedAt: string;
};
export type AssistantText =
    | AssistantWritten
    | AssistantCorrected
    | AssistantUnlocked;

// === Links in a period's prose ================================================
// The targets the server resolved for the period's links (by href). A link
// not listed renders as plain text.

export type JournalLink =
    | {
          readonly type: "resource";
          readonly href: string;
          readonly ref: ResourceRef;
      }
    | {
          readonly type: "conversation";
          readonly href: string;
          readonly id: string;
      };

// === What lies under a period =================================================

// One thing that happened on a day (the "Vad hände" list).
export type JournalActivity = {
    readonly kind: "source" | "material" | "package" | "conversation";
    readonly id: string;
    readonly title: string;
    readonly at: string;
};
// One shorter summary a rollup was built from ("" = not summarized).
export type JournalChild = {
    readonly key: PeriodKey;
    readonly markdown: string;
};

type DayDetail = {
    readonly type: "activity";
    readonly activity: readonly JournalActivity[];
};
type RollupDetail = {
    readonly type: "children";
    readonly children: readonly JournalChild[];
};
type PeriodDetail = DayDetail | RollupDetail;

export type JournalPeriod = {
    readonly key: PeriodKey;
    // null: nothing summarized (yet). Never a placeholder text.
    readonly assistant: AssistantText | null;
    // The author's own markdown; "" = not written.
    readonly own: string;
    readonly links: readonly JournalLink[];
    // The period is over (an open period is not summarized yet).
    readonly closed: boolean;
    readonly detail: PeriodDetail;
};

// One read: the periods of one scale, newest first (a day with no activity is
// not listed), and whether the assistant may read the author's own text.
export type JournalPage = {
    readonly scale: JournalScale;
    readonly periods: readonly JournalPeriod[];
    readonly assistantReadsOwn: boolean;
    // The first day ("YYYY-MM-DD") of a read that reaches the next older
    // periods; null when nothing older exists.
    readonly olderFrom: string | null;
};

export type JournalPorts = {
    // `from` ("YYYY-MM-DD"): read back to that day; null = the default span.
    readonly loadJournal: (
        scale: JournalScale,
        from: string | null,
    ) => PortResult<JournalPage>;
    readonly writeOwnText: (key: PeriodKey, text: string) => PortResult<void>;
    readonly eraseOwnText: (key: PeriodKey) => PortResult<void>;
    readonly correctAssistantText: (
        key: PeriodKey,
        markdown: string,
    ) => PortResult<void>;
    readonly unlockAssistantText: (key: PeriodKey) => PortResult<void>;
    readonly allowAssistantToReadOwn: () => PortResult<void>;
    readonly disallowAssistantToReadOwn: () => PortResult<void>;
    // Erase every own text in the workspace (privacy).
    readonly eraseAllOwnTexts: () => PortResult<void>;
    // Ask the assistant to summarize one period now.
    readonly summarizePeriod: (key: PeriodKey) => PortResult<SummarizeOutcome>;
};

// What the server answered to "summarize this period now" (a transport-level
// success). Only `summary_written` leaves a summary behind; every other leaf
// is a named way the run did not produce one.
export type SummarizeOutcome =
    | { readonly type: "summary_written" }
    | { readonly type: "summary_empty" }
    | { readonly type: "summary_refused" }
    | { readonly type: "summary_agent_failed" }
    | { readonly type: "summary_append_failed" };

// The stable identity of a period ("2026-10-01", "2026-10", "2026") — also
// the wire key.
const pad = (n: number): string => String(n).padStart(2, "0");
export const periodId = (key: PeriodKey): string => {
    switch (key.scale) {
        case "day":
            return `${key.year}-${pad(key.month)}-${pad(key.day)}`;
        case "month":
            return `${key.year}-${pad(key.month)}`;
        case "year":
            return `${key.year}`;
    }
};

// A period's first day ("YYYY-MM-DD").
export const periodStart = (key: PeriodKey): string => {
    switch (key.scale) {
        case "day":
            return periodId(key);
        case "month":
            return `${periodId(key)}-01`;
        case "year":
            return `${key.year}-01-01`;
    }
};
